<template>
  <div id="scan-order" class="order-steps-container">
    <arch
      @go-to-step="goToStep"
      :titles="steps"
      :activeStep="activeStep"
      :errors="errors"
      @submit-order="sendOrder"
    ></arch>
    <section>
      <router-view></router-view>
    </section>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useScanOrderStore } from '@/features/scanOrder/ScanOrder.store'
import { EventBus } from '@/eventbus/EventBus'

import Arch from '@/components/Arch.vue'

import { saveScanOrder } from './ScanOrderApi'

import isEmpty from 'lodash-es/isEmpty'
import cloneDeep from 'lodash-es/cloneDeep'
import OrderMixin from '@/mixins/OrderMixin'

import SendingOrder from '@/dialogs/SendingOrder.vue'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import OrderConfirm from '@/dialogs/OrderConfirm.vue'
import { useShopsStore } from '@/stores/Shops.store'

export default {
  data() {
    return {
      step: 'step1',
      steps: ['Ophalen', 'Opleveren', 'Factureren', 'Controleren'],
      inProgress: false
    }
  },
  mixins: [OrderMixin],
  components: {
    Arch
  },
  methods: {
    goToStep(step) {
      switch (step) {
        case 'step1':
          this.$router.push({
            name: 'scanOrderPickUp'
          })
          break
        case 'step2':
          this.$router.push({
            name: 'scanOrderDeliver'
          })
          break
        case 'step3':
          this.$router.push({
            name: 'scanOrderInvoice'
          })
          break
        case 'step4':
          this.$router.push({
            name: 'scanOrderConfirm'
          })
          break
      }
    },
    updateOrder(key, val) {
      this.scanOrderStore.updateOrderDetails(key, val)
    },
    sendOrder() {
      if (this.inProgress) return
      this.inProgress = true
      const order = this.prepareOrder(this.orderDetails)

      if (!order) {
        this.inProgress = false
        return
      }

      if (!order.medium.isEmail) {
        delete order.medium.emailAddress
      }

      this.$modal.show(SendingOrder, null, {
        height: 120,
        width: 280,
        adaptive: true,
        name: 'sending',
        clickToClose: false
      })

      const isDummy = this.rootStore.theme?.customFields?.checkOut?.dummyOrder

      const orderRequest = isDummy
        ? new Promise((resolve) =>
            setTimeout(() => resolve({ dummy: true }), 800)
          )
        : saveScanOrder(order)

      orderRequest.then(
        (resp) => {
          this.inProgress = false
          this.$modal.hideAll()
          this.scanOrderStore.resetOrder()
          this.$modal.show(
            OrderConfirm,
            {},
            {
              width: 360,
              adaptive: true,
              height: 'auto'
            },
            {
              'before-close': () => {
                this.step = 'step1'
              }
            }
          )
        },
        (resp) => {
          this.inProgress = false
          this.$modal.hide('sending')
          this.$modal.show(
            ErrorMessage,
            {
              title: 'Probleem bij het plaatsen van uw order',
              text: resp.message
            },
            {
              height: 250,
              width: 380,
              adaptive: true
            }
          )
        }
      )
    }
  },
  computed: {
    ...mapStores(useScanOrderStore, useShopsStore),
    shop() {
      return this.scanOrderStore.shop
    },
    orderDetails() {
      return this.scanOrderStore.orderDetails
    },
    errors() {
      return this.scanOrderStore.orderErrors.length
    },
    shops() {
      return this.shopsStore ? this.shopsStore.shopsList : []
    },
    activeStep() {
      if (this.$route.name === 'scanOrderPickUp') {
        return 'step1'
      } else if (this.$route.name === 'scanOrderDeliver') {
        return 'step2'
      } else if (this.$route.name === 'scanOrderInvoice') {
        return 'step3'
      } else if (this.$route.name === 'scanOrderConfirm') {
        return 'step4'
      }
    },
    customerNumber() {
      return this.scanOrderStore.orderDetails.invoiceAddress.newCustomer
        ? 0
        : this.scanOrderStore.orderDetails.invoiceAddress.customerNumber || 0
    }
  },
  created() {
    if (isEmpty(this.orderDetails.pickUpAddress)) {
      const pickUpAddress = this.setDeliveryAddress()
      this.scanOrderStore.updateOrderDetails('pickUpAddress', pickUpAddress)
      this.scanOrderStore.updateOrderDetails('deliveryAddress', pickUpAddress)
    }

    if (isEmpty(this.orderDetails.invoiceAddress)) {
      const invoiceDetails = this.getDefaultInvoiceDetails()

      this.scanOrderStore.updateOrderDetails(
        'invoiceAddress',
        invoiceDetails.address
      )

      this.scanOrderStore.updateProject(invoiceDetails.project)

      this.scanOrderStore.updateOrderDetails(
        'reference',
        invoiceDetails.reference
      )
    }

    this.scanOrderStore.getTheShop({
      deliveryAddress: this.orderDetails.deliveryAddress,
      invoiceId: this.customerNumber
    })

    EventBus.on('submitOrder', this.sendOrder)
  },
  unmounted() {
    EventBus.off('submitOrder', this.sendOrder)
  },
  watch: {
    'orderDetails.pickUpAddress': function () {
      this.scanOrderStore.getTheShop({
        deliveryAddress: this.orderDetails.pickUpAddress,
        invoiceId: this.customerNumber
      })
    },
    'orderDetails.invoiceAddress': function () {
      this.scanOrderStore.getTheShop({
        deliveryAddress: this.orderDetails.pickUpAddress,
        invoiceId: this.customerNumber
      })
    },
    shop: function (val) {
      this.scanOrderStore
        .getThePickUpDates({
          shopId: this.shop.id,
          invoiceId: this.customerNumber
        })
        .then((dates) => {
          if (isEmpty(this.orderDetails.pickUpDate)) {
            let puDate = {}
            puDate = dates.find((date) => {
              return date.active
            })

            puDate.time = puDate.times[0].time

            this.scanOrderStore.updateOrderDetails('pickUpDate', puDate)
          }
        })
    },
    'orderDetails.pickUpDate.value': function (val) {
      if (val === 'spoed') {
        return
      }

      this.scanOrderStore
        .getTheDeliveryDates({
          shopId: this.shop.id,
          invoiceId: this.orderDetails.invoiceAddress.id,
          secure: false,
          offset: {
            date: this.orderDetails.pickUpDate.value,
            time: this.orderDetails.pickUpDate.time
          }
        })
        .then((dates) => {
          let delDate = {}
          delDate = dates.find((date) => {
            return date.active
          })

          delDate.time = delDate.times[0].time

          this.scanOrderStore.updateOrderDetails('deliveryDate', delDate)
        })
    }
  }
}
</script>
<style lang="scss"></style>
